/**
 * Facturas (Invoices) — listado de facturas emitidas desde Pedidos.
 *
 * Para CREAR una factura, el usuario va a /orders, presiona "Facturar" en
 * un pedido (estado=alistando) y se abre el diálogo editable. Esta página
 * solo muestra el historial, permite ver el detalle y anular.
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Receipt, Ban, FileText, Undo2, Download } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { WhatsAppShareButton } from "@/components/WhatsAppShare";

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Invoices() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [anularTarget, setAnularTarget] = useState(null);
  const [anularMotivo, setAnularMotivo] = useState("");

  // Devolución (return) dialog state — admin picks lines + qty per line
  const [devTarget, setDevTarget] = useState(null);
  const [devLines, setDevLines] = useState([]);
  const [devMotivo, setDevMotivo] = useState("");
  const [devSaving, setDevSaving] = useState(false);

  const openDevDialog = (inv) => {
    setDevTarget(inv);
    setDevLines((inv.lineas || []).map(l => ({
      codigo: l.codigo,
      producto: l.producto,
      cantidad_facturada: l.cantidad,
      cantidad: 0,
      es_bonificacion: l.es_bonificacion,
    })));
    setDevMotivo("");
  };

  const submitDev = async () => {
    if (!devTarget) return;
    const lineas = devLines.filter(l => parseFloat(l.cantidad) > 0)
      .map(l => ({ codigo: l.codigo, cantidad: parseFloat(l.cantidad) || 0 }));
    if (lineas.length === 0) { toast.error("Indicá al menos una cantidad a devolver"); return; }
    setDevSaving(true);
    try {
      const { data } = await api.post("/returns", {
        invoice_id: devTarget.id,
        lineas,
        motivo: devMotivo,
      });
      toast.success(`Devolución ${data.numero} registrada · Nota de Crédito ${data.credit_note_numero} por ${formatMoney(data.total)}`);
      setDevTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar devolución");
    } finally { setDevSaving(false); }
  };

  // PDF + Print helpers — reuse the auth token via fetch (HTML <a download>
  // can't send Authorization headers).
  const downloadInvoicePdf = async (inv) => {
    const token = localStorage.getItem("token");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `factura-${inv.numero}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const printInvoicePdf = async (inv) => {
    const token = localStorage.getItem("token");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const win = window.open(objectUrl, "_blank");
      if (win) win.addEventListener("load", () => win.print(), { once: true });
    } catch { toast.error("No se pudo abrir la impresión"); }
  };

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/invoices");
      setList(data);
    } catch { toast.error("Error al cargar facturas"); }
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return list;
    return list.filter(inv =>
      (inv.numero || "").toLowerCase().includes(q) ||
      (inv.nombre_cliente || "").toLowerCase().includes(q) ||
      (inv.nombre_vendedor || "").toLowerCase().includes(q),
    );
  }, [list, search]);

  const submitAnular = async () => {
    if (!anularTarget) return;
    try {
      await api.post(`/invoices/${anularTarget.id}/anular`, { motivo: anularMotivo });
      toast.success(`Factura ${anularTarget.numero} anulada — stock revertido`);
      setAnularTarget(null);
      setAnularMotivo("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al anular factura");
    }
  };

  if (loading) return <PageLoader label="Cargando facturas…" testid="invoices-loader" />;

  const totalEmitidas = list.filter(i => i.estado === "emitida").reduce((s, i) => s + (i.total || 0), 0);
  const cntEmitidas = list.filter(i => i.estado === "emitida").length;
  const cntAnuladas = list.filter(i => i.estado === "anulada").length;

  return (
    <div data-testid="invoices-page">
      <div className="mb-4">
        <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Facturas
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Para emitir una factura, abrí un pedido en <strong>Pedidos</strong> y hacé clic en <strong>Facturar</strong>. Al emitir se descuenta del inventario.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <Card className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">FACTURAS EMITIDAS</p>
            <p className="text-2xl font-black">{cntEmitidas}</p>
          </div>
          <Receipt className="h-6 w-6 text-[rgb(0,47,167)]" />
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">TOTAL FACTURADO</p>
            <p className="text-2xl font-black text-[rgb(0,47,167)]">{formatMoney(totalEmitidas)}</p>
          </div>
          <FileText className="h-6 w-6 text-[rgb(0,47,167)]" />
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">ANULADAS</p>
            <p className="text-2xl font-black text-rose-700">{cntAnuladas}</p>
          </div>
          <Ban className="h-6 w-6 text-rose-600" />
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por número, cliente o vendedor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm pl-9"
            data-testid="invoices-search"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2 px-3 font-medium text-slate-700">Número</th>
                <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                <th className="text-left py-2 px-3 font-medium text-slate-700">Cliente</th>
                <th className="text-left py-2 px-3 font-medium text-slate-700">Vendedor</th>
                <th className="text-right py-2 px-3 font-medium text-slate-700">Subtotal</th>
                <th className="text-right py-2 px-3 font-medium text-slate-700">IVA</th>
                <th className="text-right py-2 px-3 font-medium text-slate-700">Total</th>
                <th className="text-left py-2 px-3 font-medium text-slate-700">Estado</th>
                <th className="text-right py-2 px-3 font-medium text-slate-700">Saldo CxC</th>
                <th className="text-right py-2 px-3 font-medium text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(inv => (
                <tr key={inv.id} onClick={() => setDetail(inv)} className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${inv.estado === "anulada" ? "bg-slate-50/60 opacity-60" : ""}`} data-testid={`invoice-row-${inv.numero}`}>
                  <td className="py-2 px-3 mono font-medium">{inv.numero}</td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(inv.created_at)}</td>
                  <td className="py-2 px-3">{inv.nombre_cliente || "-"}</td>
                  <td className="py-2 px-3 text-slate-600">{inv.nombre_vendedor || "-"}</td>
                  <td className="py-2 px-3 text-right mono">{formatMoney(inv.subtotal)}</td>
                  <td className="py-2 px-3 text-right text-slate-600 mono">{formatMoney(inv.iva_total)}</td>
                  <td className="py-2 px-3 text-right font-bold mono">{formatMoney(inv.total)}</td>
                  <td className="py-2 px-3">
                    {inv.estado === "anulada" ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200">Anulada</span>
                    ) : inv.estado_cobro === "pagada" ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Pagada</span>
                    ) : inv.estado_cobro === "parcial" ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">Parcial</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">Pendiente</span>
                    )}
                  </td>
                  <td className={`py-2 px-3 text-right font-medium mono ${(inv.saldo_pendiente || 0) > 0 ? "text-rose-700" : "text-slate-400"}`}>
                    {inv.estado === "anulada" ? "-" : formatMoney(inv.saldo_pendiente ?? inv.total)}
                  </td>
                  <td className="py-2 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" className="rounded-sm h-7" onClick={() => setDetail(inv)} data-testid={`invoice-view-${inv.numero}`}>Ver</Button>
                      <WhatsAppShareButton type="invoice" id={inv.id} label="WA" size="sm" testid={`invoice-wa-${inv.numero}`} />
                      {inv.estado === "emitida" && (
                        <>
                          <Button size="sm" variant="ghost" className="rounded-sm h-7 text-amber-700 hover:bg-amber-50" onClick={() => openDevDialog(inv)} data-testid={`invoice-return-${inv.numero}`} title="Registrar devolución (genera Nota de Crédito)">
                            <Undo2 className="h-3 w-3 mr-1" />Devolver
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-sm h-7 text-rose-600 hover:bg-rose-50" onClick={() => { setAnularTarget(inv); setAnularMotivo(""); }} data-testid={`invoice-anular-${inv.numero}`}>
                            <Ban className="h-3 w-3 mr-1" />Anular
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">Aún no hay facturas. Emití la primera desde Pedidos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[rgb(0,47,167)]" />
              Factura {detail?.numero}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 mt-2 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Cliente</p><p className="font-medium">{detail.nombre_cliente || "-"}</p>{detail.cliente_telefono && <p className="text-slate-500">📞 {detail.cliente_telefono}</p>}</div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Vendedor</p><p className="font-medium">{detail.nombre_vendedor || "-"}</p>{detail.codigo_vendedor && <p className="text-slate-500 mono">{detail.codigo_vendedor}</p>}</div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Fecha</p><p className="font-medium">{detail.fecha}</p></div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Estado</p><p className="font-medium">{detail.estado}</p></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-slate-200 bg-slate-50"><th className="text-left py-2 px-2">Código</th><th className="text-left py-2 px-2">Producto</th><th className="text-right py-2 px-2">Cant.</th><th className="text-right py-2 px-2">Precio</th><th className="text-right py-2 px-2">IVA%</th><th className="text-right py-2 px-2">Subt.</th></tr></thead>
                  <tbody>
                    {detail.lineas.map((l, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${l.es_bonificacion ? "bg-emerald-50/40" : ""}`}>
                        <td className="py-2 px-2 mono">{l.codigo}</td>
                        <td className="py-2 px-2">{l.producto}{l.es_bonificacion && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">GRATIS</span>}</td>
                        <td className="py-2 px-2 text-right">{l.cantidad}</td>
                        <td className="py-2 px-2 text-right">{formatMoney(l.precio_unitario)}</td>
                        <td className="py-2 px-2 text-right text-slate-500">{l.iva_pct}%</td>
                        <td className="py-2 px-2 text-right font-medium">{formatMoney(l.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan={5} className="py-2 px-2 text-right text-slate-500">Subtotal</td><td className="py-2 px-2 text-right">{formatMoney(detail.subtotal)}</td></tr>
                    <tr><td colSpan={5} className="py-2 px-2 text-right text-slate-500">IVA</td><td className="py-2 px-2 text-right">{formatMoney(detail.iva_total)}</td></tr>
                    <tr><td colSpan={5} className="py-2 px-2 text-right font-bold uppercase tracking-wider">Total</td><td className="py-2 px-2 text-right font-black text-base text-[rgb(0,47,167)]">{formatMoney(detail.total)}</td></tr>
                  </tfoot>
                </table>
              </div>
              {detail.estado === "anulada" && detail.anulada_motivo && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2">
                  <strong>Motivo de anulación:</strong> {detail.anulada_motivo}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button type="button" variant="outline" onClick={() => printInvoicePdf(detail)} className="rounded-sm" data-testid="preview-invoice-print">
                  <FileText className="h-4 w-4 mr-2" />Imprimir
                </Button>
                <Button type="button" onClick={() => downloadInvoicePdf(detail)} className="btn-primary rounded-sm" data-testid="preview-invoice-pdf">
                  <Download className="h-4 w-4 mr-2" />Descargar PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Anular confirmation */}
      <AlertDialog open={!!anularTarget} onOpenChange={(o) => !o && setAnularTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Anular factura {anularTarget?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esto revertirá el stock descontado y liberará el pedido origen para que pueda re-facturarse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={2} value={anularMotivo} onChange={(e) => setAnularMotivo(e.target.value)} placeholder="Motivo de anulación…" className="rounded-sm" data-testid="anular-motivo-input" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={submitAnular} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="anular-confirm-button">Anular factura</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Devolución dialog. Admin picks per-line qty to return. Each line
         is validated server-side against what's still returnable (factured
         minus already-returned). Generates DEV-NNNNNN + NC-NNNNNN. */}
      <Dialog open={!!devTarget} onOpenChange={(o) => !o && setDevTarget(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              Devolución de {devTarget?.numero}
            </DialogTitle>
          </DialogHeader>
          {devTarget && (
            <div className="mt-2 space-y-3">
              <p className="text-xs text-slate-500">
                Indicá cuántas unidades de cada línea está devolviendo el cliente. Al confirmar, el stock vuelve al inventario y se genera una <strong>Nota de Crédito</strong> que se aplica contra esta factura.
              </p>
              <div className="overflow-x-auto max-h-72 border border-slate-200 rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr><th className="text-left py-2 px-2 font-medium">Código</th><th className="text-left py-2 px-2 font-medium">Producto</th><th className="text-right py-2 px-2 font-medium w-20">Facturado</th><th className="text-right py-2 px-2 font-medium w-24">Devolver</th></tr>
                  </thead>
                  <tbody>
                    {devLines.map((l, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${l.es_bonificacion ? "bg-emerald-50/40" : ""}`}>
                        <td className="py-1.5 px-2 mono">{l.codigo}</td>
                        <td className="py-1.5 px-2">
                          {l.producto}
                          {l.es_bonificacion && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">GRATIS</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right mono text-slate-500">{l.cantidad_facturada}</td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" min="0" max={l.cantidad_facturada} step="any" value={l.cantidad}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDevLines(prev => prev.map((x, k) => k === i ? { ...x, cantidad: v } : x));
                            }}
                            className="h-7 rounded-sm text-right mono w-20" data-testid={`dev-line-qty-${i}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Motivo</label>
                <Textarea rows={2} value={devMotivo} onChange={(e) => setDevMotivo(e.target.value)} placeholder="Ej: Producto defectuoso, no era lo solicitado, vencido…" className="rounded-sm" data-testid="dev-motivo-input" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDevTarget(null)} className="rounded-sm" disabled={devSaving}>Cancelar</Button>
                <Button type="button" onClick={submitDev} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" disabled={devSaving} data-testid="dev-submit-button">
                  <Undo2 className="h-4 w-4 mr-2" />{devSaving ? "Registrando…" : "Registrar devolución"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
